import React from 'react';
import { Box, BoxProps } from '@chakra-ui/react';

type Intensity = 'low' | 'medium' | 'strong';

interface GlassCardProps extends BoxProps {
  /** Intensidade do blur e da opacidade do glass. */
  intensity?: Intensity;
  /** Cor opcional sobreposta sutilmente (ex: cor representativa do módulo). */
  tint?: string;
  /** Aplicar elevação no hover. */
  hoverable?: boolean;
  /** Realça a BORDA do card (ex.: alvo de deep-link `?destaque=`). Persiste no hover. */
  destacado?: boolean;
  /**
   * Ocupa a altura toda do pai, conteudo incluido: cards de uma mesma linha do grid
   * ficam do mesmo tamanho e o rodape pode ir para o fundo (`mt="auto"`).
   */
  preencherAltura?: boolean;
}

// Sem `backdrop-filter`, o fundo precisa ficar um pouco mais opaco para o card
// continuar se destacando da pagina — a translucidez some, o contraste nao.
const BG_MAP: Record<Intensity, string> = {
  low: 'rgba(255, 255, 255, 0.72)',
  medium: 'rgba(255, 255, 255, 0.82)',
  strong: 'rgba(255, 255, 255, 0.92)',
};

/**
 * Container no estilo Mac/Apple.
 *
 * - Background translúcido, borda finíssima e sombra azul sutil (cor da marca)
 * - SEM `backdrop-filter`: ver a nota abaixo antes de trazer o blur de volta
 *
 * Por que o blur saiu (10/08/2026, segunda vez que este bug aparece):
 * o card ficava em branco — so o painel fosco, sem conteudo — e o borrao ainda
 * vazava para fora da caixa, cobrindo o card vizinho. O Chromium pinta
 * `backdrop-filter` amostrando um "backdrop root"; quando a camada composta e
 * criada e destruida (fim da animacao de entrada, hover entrando e saindo, card
 * cruzando a viewport no scroll), o elemento volta rasterizado contra um
 * backdrop vazio.
 *
 * Em 05/08 a tentativa foi trocar `translateY` por `top` no hover, mirando so um
 * dos gatilhos. Nao bastou: cada card entra dentro de um `motion.div` que anima
 * `transform` + `opacity`, entao o ciclo de camada acontece mesmo sem hover.
 *
 * A troca custa quase nada de visual: o fundo da pagina e branco com manchas
 * radiais quase imperceptiveis (`GlassPageBg`), entao nunca houve nada atras dos
 * cards para desfocar. Se algum dia um GlassCard for usado por cima de conteudo
 * de verdade (drawer sobre tabela), o blur volta ali, naquele uso — nao aqui na
 * base, que hoje tem ~490 usos.
 */
export const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  (
    {
      intensity = 'medium',
      tint,
      hoverable = false,
      destacado = false,
      preencherAltura = false,
      children,
      ...rest
    },
    ref,
  ) => {
    return (
      <Box
        ref={ref}
        bg={BG_MAP[intensity]}
        borderWidth={destacado ? '2px' : '1px'}
        borderColor={destacado ? 'purple.400' : 'rgba(255, 255, 255, 0.6)'}
        borderRadius="3xl"
        boxShadow="0 1px 0 0 rgba(255, 255, 255, 0.9) inset, 0 12px 40px -12px rgba(27, 136, 245, 0.18), 0 4px 12px -4px rgba(15, 23, 42, 0.06)"
        position="relative"
        top={0}
        h={preencherAltura ? '100%' : undefined}
        overflow="hidden"
        // `all` fazia toda propriedade animar, inclusive as que so precisam
        // trocar de valor. Lista explicita: menos repaint e nada anima por
        // acidente.
        transition="top 0.32s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.32s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.32s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={
          hoverable
            ? {
                // Sobe com `top`, NAO com `translateY`: `transform` promove o
                // card a camada composta a cada hover e descarta ao sair, e esse
                // vai-e-vem era metade do bug do backdrop-filter (ver nota no
                // topo). Com o blur fora a promocao ja nao quebra a pintura, mas
                // `top` continua sendo o deslocamento mais barato — mesmos 3px
                // sem criar e destruir camada.
                // Nao afeta o layout dos vizinhos: `top` em position:relative
                // desloca a pintura, nao o fluxo.
                top: '-3px',
                boxShadow:
                  '0 1px 0 0 rgba(255, 255, 255, 1) inset, 0 18px 50px -12px rgba(27, 136, 245, 0.28), 0 6px 20px -4px rgba(15, 23, 42, 0.08)',
                borderColor: destacado ? 'purple.400' : 'rgba(27, 136, 245, 0.35)',
              }
            : undefined
        }
        {...rest}
      >
        {tint && (
          <Box
            position="absolute"
            inset={0}
            bg={tint}
            opacity={0.08}
            pointerEvents="none"
            zIndex={0}
          />
        )}
        <Box position="relative" zIndex={1} h={preencherAltura ? '100%' : undefined}>
          {children}
        </Box>
      </Box>
    );
  },
);

GlassCard.displayName = 'GlassCard';
